/**
 * Cloud POS RBAC aligned with GET Authenticate/permission-catalog and login PermissionGroups.
 */

export type PermissionGroupRow = { group: string; permissions: string[] };

export type CloudPosPermSpec =
  | { group: string; permission: string }
  /** Any permission within the catalog group (e.g. any inventory module). */
  | { group: string; anyInGroup: true };

export function parsePermissionGroupsFromStorage(): PermissionGroupRow[] | null {
  try {
    const raw = sessionStorage.getItem('impersonatePermissionGroups') || localStorage.getItem("permissionGroups");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map(normalizeGroupRow).filter((g) => g.group);
  } catch {
    return null;
  }
}

function normalizeGroupRow(g: unknown): PermissionGroupRow {
  const o = g as Record<string, unknown>;
  const group = String(o.group ?? o.Group ?? "");
  const permsRaw = o.permissions ?? o.Permissions ?? [];
  const permissions = Array.isArray(permsRaw)
    ? permsRaw.map((p) => String(p)).filter(Boolean)
    : [];
  return { group, permissions };
}

/**
 * Permissions that moved to another group (mirror of the server's CloudPosPermissionCatalog.MovedPermissions):
 * old group → (permission → new group). Purchasing left "InventoryProducts" for its own "Purchases" group, and
 * the stock screens (stock list, transfer, opening stock) for "Stock".
 */
export const MOVED_PERMISSION_GROUPS: Record<string, Record<string, string>> = {
  InventoryProducts: {
    PurchaseInventory: "Purchases",
    PurchaseHistory: "Purchases",
    InventoryReturn: "Purchases",
    PurchaseOrderGenerator: "Purchases",
    GoodsReceivedNote: "Purchases",
    StockItems: "Stock",
    StockTransfer: "Stock",
    OpeningStock: "Stock",
  },
};

export function buildPermissionLookup(groups: PermissionGroupRow[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const g of groups) {
    if (!g.group) continue;
    if (!map.has(g.group)) map.set(g.group, new Set());
    const set = map.get(g.group)!;
    for (const p of g.permissions) {
      set.add(p);
      if (g.group === "StaffSecurity" && p === "EmployeeManagement") {
        set.add("UserManagement");
      }
    }
  }
  // A moved permission counts under its old AND its new group, so checks work whichever group the
  // server (older or newer than this page) lists it under.
  for (const [oldGroup, moved] of Object.entries(MOVED_PERMISSION_GROUPS)) {
    for (const [permission, newGroup] of Object.entries(moved)) {
      const inOld = map.get(oldGroup)?.has(permission) ?? false;
      const inNew = map.get(newGroup)?.has(permission) ?? false;
      if (inOld === inNew) continue;
      const target = inOld ? newGroup : oldGroup;
      if (!map.has(target)) map.set(target, new Set());
      map.get(target)!.add(permission);
    }
  }
  return map;
}

export function hasPermission(
  lookup: Map<string, Set<string>>,
  group: string,
  permission: string
): boolean {
  const set = lookup.get(group);
  if (!set) return false;
  if (set.has(permission)) return true;
  if (group === "StaffSecurity" && permission === "UserManagement" && set.has("EmployeeManagement")) {
    return true;
  }
  return false;
}

export function hasAnyPermissionInGroup(lookup: Map<string, Set<string>>, group: string): boolean {
  const set = lookup.get(group);
  return !!set && set.size > 0;
}

/**
 * Legacy sessions / developer: do not lock the UI when PermissionGroups were not persisted.
 */
export function shouldBypassCloudPosPermissionChecks(groupsFromStorage: PermissionGroupRow[] | null): boolean {
  try {
    const rolesRaw = localStorage.getItem("userRoles") || "";
    const roles = rolesRaw.split(",").map((r) => r.trim().toLowerCase()).filter(Boolean);
    if (roles.includes("developer")) return true;
    if (roles.includes("storeowner")) return true;
    if (roles.includes("companyadmin")) return true;
  } catch {
    /* ignore */
  }
  if (groupsFromStorage === null) return true;
  return false;
}

export function meetsCloudPosPermission(
  lookup: Map<string, Set<string>>,
  bypass: boolean,
  spec: CloudPosPermSpec | undefined
): boolean {
  if (!spec) return true;
  if (bypass) return true;
  if ("anyInGroup" in spec && spec.anyInGroup) return hasAnyPermissionInGroup(lookup, spec.group);
  if ("permission" in spec) return hasPermission(lookup, spec.group, spec.permission);
  return true;
}

export function canAccessSetupHub(
  lookup: Map<string, Set<string>>,
  bypass: boolean,
  setupCardPerms: ReadonlyArray<{ group: string; permission: string }>
): boolean {
  if (bypass) return true;
  if (setupCardPerms.length === 0) return true;
  return setupCardPerms.some(({ group, permission }) => hasPermission(lookup, group, permission));
}

/** POS terminal card has `path: null`; use dedicated perm. */
export const SETUP_POS_TERMINAL_PERM = { group: "PosOperations", permission: "PosTerminal" } as const;

/**
 * Barcode Stock Audit permission group.
 *
 * These mirror `Domain/Entities/Permissions/CloudPosPermissionCatalog.cs` exactly — the
 * server is the authority, silently drops any key not in its catalog, and re-checks every
 * one of these on the API side (StockAuditController.HasStockAuditPermissionAsync). The
 * constants exist so pages never hand-type the group/key strings.
 */
export const STOCK_AUDIT_GROUP = "StockAudit";

export const STOCK_AUDIT_PERMISSIONS = {
  /** Routes: list, detail, lines, scans, unknown barcodes, variance report, export, events. */
  view: "StockAuditView",
  /** Create the audit and take its (irreversible) stock snapshot. */
  create: "StockAuditCreate",
  /** Scan / count / manual count / void a scan / complete counting / submit for review. */
  scan: "StockAuditScan",
  /** Review variances, bulk review, resolve unknown barcodes, manage pack barcodes. */
  review: "StockAuditReview",
  /** Approve, reject, reopen. */
  approve: "StockAuditApprove",
  /** Finalize — the only path that posts stock and GL adjustments. */
  finalize: "StockAuditFinalize",
  /** Cancel a non-finalized audit. */
  cancel: "StockAuditCancel",
} as const;

/**
 * Stock Manual Consumption permission group — stock used up WITHOUT a sale (service use,
 * internal use, damage, expiry, samples).
 *
 * Mirrors `Domain/Entities/Permissions/CloudPosPermissionCatalog.cs` group "StockConsumption"
 * exactly. The server re-checks every one of these in
 * StockConsumptionController.HasPermissionAsync, so hiding a button here is convenience only.
 */
export const STOCK_CONSUMPTION_GROUP = "StockConsumption";

export const STOCK_CONSUMPTION_PERMISSIONS = {
  /** List, details and the report. */
  view: "StockConsumptionView",
  /** Record a consumption — this is what actually deducts stock. */
  create: "StockConsumptionCreate",
  /** Edit — the server reverses the revision in effect and re-applies the new one. */
  edit: "StockConsumptionEdit",
  /** Cancel — restores the stock, keeps the document. */
  cancel: "StockConsumptionCancel",
  /** Soft-delete a CANCELLED document (a completed one can never be deleted). */
  delete: "StockConsumptionDelete",
} as const;

/**
 * Exchange module permission group (mirrors CloudPosPermissionCatalog.cs "Exchange"; the
 * server re-checks every key in ExchangeController.HasExchangePermissionAsync).
 */
export const EXCHANGE_GROUP = "Exchange";
export const EXCHANGE_PERMISSIONS = {
  /** History list, details, report. */
  view: "ExchangeView",
  /** The exchange terminal (quote + create). */
  create: "ExchangeCreate",
  /** Approve an exchange whose difference is owed back to the customer. */
  refund: "ExchangeRefund",
  /** Cancel (reverse) a completed exchange. */
  cancel: "ExchangeCancel",
  /** Exchange Dashboard (analytics). */
  dashboard: "ExchangeDashboard",
  /** Manage the store's exchange policy (Setup → Exchange → Exchange Rules). */
  rules: "ExchangeRules",
} as const;

/**
 * Deals module permission group (Setup → Deals). Mirrors CloudPosPermissionCatalog.cs "Deals";
 * the server re-checks every key in DealsController.HasPermissionAsync, so hiding a button here
 * is convenience only. Creating / editing one deal stays on the item screen.
 */
export const DEALS_GROUP = "Deals";
export const DEALS_PERMISSIONS = {
  /** The deals list and deal details. */
  view: "DealsView",
  /** Import deals from Excel (template download included). */
  import: "DealsImport",
  /** Export deals to Excel. */
  export: "DealsExport",
} as const;

/**
 * Opening balances of the payment accounts live on the Owner Transactions screen (a tab), so
 * SEEING them is that screen's permission; adding / editing / removing needs this key.
 * Mirrors CloudPosPermissionCatalog.cs "FinancialManagement"; the server re-checks it.
 */
export const OPENING_BALANCE_MANAGE_PERMISSION = { group: "FinancialManagement", permission: "OpeningBalancesManage" } as const;

/**
 * Cash management — Setup → Cash Management (confirm cash handovers, where the cash is),
 * Reports → Cash Handover Report, and the desktop's Operations → Cash Handover. Mirrors
 * CloudPosPermissionCatalog.cs "CashManagement". The server re-checks every key in
 * CashHandoverController and also gives the shared till roles (Cashier / Staff / Manager)
 * defaults that are NOT in the permission groups, so the pages ask the server
 * (`CashHandover/my-permissions`) what the user may do; these constants only drive the Setup card.
 */
export const CASH_MANAGEMENT_GROUP = "CashManagement";
export const CASH_MANAGEMENT_PERMISSIONS = {
  /** Hand a closed shift's cash over (the desktop till). */
  handover: "CashHandoverCreate",
  /** The Cash Management screen: balances and handovers. */
  view: "CashManagementView",
  /** Confirm / reject / reverse handovers (implies view on the server). */
  receive: "CashHandoverReceive",
  /** Cash Handover Report. */
  reports: "CashReports",
  /** Send cash (float) to a branch (implies view on the server). */
  float: "CashFloatSend",
} as const;

/**
 * Appointments module — opt-in (Settings → POS Settings → Modules → Appointments; off by default).
 * Mirrors CloudPosPermissionCatalog.cs "Appointments". The server re-checks every key on the booking
 * endpoints and gives the built-in roles defaults that are NOT in the permission groups (Manager: all;
 * Cashier: dashboard, view, manage, clients; Staff: dashboard), so the screens, the Setup hub and
 * /reports ask the server (`Bookings/my-permissions`); these constants drive the side menu (custom
 * roles) and the route map only.
 */
export const APPOINTMENTS_GROUP = "Appointments";
export const APPOINTMENT_PERMISSIONS = {
  /** The appointment dashboard. */
  dashboard: "AppointmentDashboard",
  /** Sales, revenue and commission totals on the dashboard and in the staff stats. */
  dashboardSales: "AppointmentDashboardSales",
  /** Every staff member's appointments in the branch (without it: only the user's own). */
  view: "AppointmentView",
  /** Book, edit, reschedule, cancel, any status, take payment (implies view on the server). */
  manage: "AppointmentManage",
  /** Clients: list, history, spend and visits. */
  clients: "AppointmentClients",
  /** Working hours, blocked time, staff services and service durations. */
  staffSchedule: "AppointmentStaffSchedule",
} as const;

/**
 * Commission module — opt-in, its own module; available while the Commission switch is on. Mirrors
 * CloudPosPermissionCatalog.cs "Commission"; the server re-checks every key (CommissionController)
 * and its my-permissions answers carry the built-in role defaults, like Appointments above.
 */
export const COMMISSION_GROUP = "Commission";
export const COMMISSION_PERMISSIONS = {
  /** Commission policies: create, edit, activate, preview. */
  setup: "CommissionSetup",
  /** Approve / reject commission entries, adjustments. */
  approve: "CommissionApprove",
  /** Pay commission out (settlements) and void settlements. */
  settle: "CommissionSettle",
  /** Everyone's commission in the user's branch scope (Reports → Commission report). */
  report: "CommissionReport",
  /** The user's own commission statement. */
  ownStatement: "CommissionOwnStatement",
} as const;

/**
 * Setup dashboard card targets → catalog permission (paths matched against `card.path`).
 * Must stay in sync with `Setup/index.tsx` navigation targets.
 */
export const SETUP_ROUTE_PERMISSION: Record<string, { group: string; permission: string }> = {
  "/management/setup/business/profile": { group: "QuickSetup", permission: "BusinessProfile" },
  "/management/setup/system/allbranches": { group: "QuickSetup", permission: "BranchSetup" },
  "/management/setup/master/rolelist": { group: "QuickSetup", permission: "UserRoles" },
  "/management/setup/system/allcategory": { group: "InventoryProducts", permission: "ProductCategories" },
  "/management/setup/system/allunits": { group: "InventoryProducts", permission: "Units" },
  "/management/setup/system/allsuppliers": { group: "InventoryProducts", permission: "Suppliers" },
  // Opening Stock is a grantable catalog permission in the "Stock" group (before, its key was not in the
  // catalog, so only owners / admins ever saw the card).
  "/management/setup/opening-stock": { group: "Stock", permission: "OpeningStock" },
  // Variant purchasing is a purchase-side capability — it reuses the Purchase permission.
  "/management/setup/variant-purchasing": { group: "Purchases", permission: "PurchaseInventory" },
  // Bulk variant creation writes the item catalog.
  "/management/setup/bulk-variant-items": { group: "InventoryProducts", permission: "Items" },
  // Recipe Management keeps the original "Production" key (every role that has it keeps its
  // recipes); Production Orders is its own module and permission. A role saved before the split
  // is granted Production Orders by the server (CloudPosPermissionClaimHelper.WithLegacyImplications).
  "/production": { group: "InventoryProducts", permission: "Production" },
  "/production-orders": { group: "InventoryProducts", permission: "ProductionOrders" },
  "/reports/recipe-mapping": { group: "InventoryProducts", permission: "Production" },
  "/settings": { group: "InventoryProducts", permission: "CompanySettings" },
  // Setup → "Invoice Designer" card (deep link to the Invoice tab; same permission as /settings)
  "/settings?tab=invoice": { group: "InventoryProducts", permission: "CompanySettings" },
  "/reports/labels": { group: "InventoryProducts", permission: "LabelPrinting" },
  "/management/setup/restaurants/floors": { group: "PosOperations", permission: "FloorsRestaurants" },
  "/management/setup/restaurants/tables": { group: "PosOperations", permission: "TablesRestaurants" },
  "/management/setup/restaurants/table-qr-codes": { group: "PosOperations", permission: "QrOrdering" },
  // Bookings reuses the Customers permission (appointment desk = customer-facing POS work);
  // visibility is additionally gated by store type in Setup/index.tsx.
  "/management/setup/bookings": { group: "PosOperations", permission: "Customers" },
  "/management/setup/system/allcustomers": { group: "PosOperations", permission: "Customers" },
  "/sales/getAll": { group: "PosOperations", permission: "SalesHistory" },
  // Exchange (return + replacement in one transaction).
  "/sale/exchange": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.create },
  "/sales/exchanges": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.view },
  "/reports/exchange-report": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.view },
  "/sales/exchange-dashboard": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.dashboard },
  "/management/exchange-rules": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.rules },
  // Deals module: the list needs "view"; import / export are gated per-button (and server-side).
  "/management/deals": { group: DEALS_GROUP, permission: DEALS_PERMISSIONS.view },
  "/sales-documents/quotations": { group: "SalesQuotations", permission: "Quotations" },
  "/sales-documents/sales-orders": { group: "SalesQuotations", permission: "SalesOrders" },
  "/sales-documents/invoices": { group: "SalesQuotations", permission: "Invoices" },
  "/sales-documents/returns": { group: "SalesQuotations", permission: "Returns" },
  "/pos/payment-methods": { group: "PosOperations", permission: "PaymentMethods" },
  "/payment-terminal": { group: "PosOperations", permission: "PaymentTerminals" },
  "/user-management": { group: "StaffSecurity", permission: "UserManagement" },
  "/salary-management": { group: "StaffSecurity", permission: "SalaryManagement" },
  "/management/cancellation-access": { group: "StaffSecurity", permission: "CancellationAccess" },
  "/management/void-orders": { group: "StaffSecurity", permission: "VoidOrdersDashboard" },
  // Table Merge/Transfer is a restaurant-tables capability, so it reuses the Tables permission —
  // owners/admins bypass and always see it; anyone permitted to manage tables can toggle it.
  // (Without an entry here the Setup filter hides the card for every non-bypass role.)
  "/management/table-operations": { group: "PosOperations", permission: "TablesRestaurants" },
  "/management/AllAccounts": { group: "FinancialManagement", permission: "ChartOfAccounts" },
  "/management/setup/equity/getall": { group: "FinancialManagement", permission: "TransactionRecords" },
  "/management/setup/owner-transactions": { group: "FinancialManagement", permission: "OwnerTransactions" },
  "/management/setup/transfer-cash": { group: "FinancialManagement", permission: "TransferCash" },
  "/management/setup/cash-management": { group: "CashManagement", permission: "CashManagementView" },
  // Setup → "Send Cash to Branch" card (deep link to the Cash Management floats tab)
  "/management/setup/cash-management?tab=floats": { group: "CashManagement", permission: "CashFloatSend" },
  // Setup → "Opening Balances" card (deep link to the Owner Transactions tab; same permission)
  "/management/setup/owner-transactions?tab=opening-balances": { group: "FinancialManagement", permission: "OwnerTransactions" },
  "/expense": { group: "FinancialManagement", permission: "ExpenseManagement" },
  "/management/ledger": { group: "FinancialManagement", permission: "LedgerManagement" },
  "/management/setup/fbr-integration": { group: "FinancialManagement", permission: "FbrPakistan" },
  "/management/setup/tax-compliance": { group: "FinancialManagement", permission: "FbrPakistan" },
  "/management/setup/zatca-integration": { group: "FinancialManagement", permission: "ZatcaSaudiArabia" },
  "/management/setup/vat-rates": { group: "FinancialManagement", permission: "VatRates" },
  "/management/setup/item-discount-rules": { group: "RulesDiscounts", permission: "ItemDiscountRules" },
  "/management/setup/item-tax-rules": { group: "RulesDiscounts", permission: "ItemTaxRules" },
  "/management/setup/customer-discount-rules": { group: "RulesDiscounts", permission: "CustomerDiscountRules" },
  "/management/setup/invoice-discount-rules": { group: "RulesDiscounts", permission: "InvoiceDiscountRules" },
  "/management/setup/invoice-tax-rules": { group: "RulesDiscounts", permission: "InvoiceTaxRules" },
  "/management/setup/tax-on-payment-methods": { group: "RulesDiscounts", permission: "TaxOnPaymentMethods" },
  "/management/setup/payment-method-tax": { group: "RulesDiscounts", permission: "PaymentMethodTax" },
  "/management/setup/service-charges": { group: "RulesDiscounts", permission: "ServiceCharges" },
  "/management/setup/stock-adjustments": { group: "RulesDiscounts", permission: "StockAdjustments" },
  "/reports/purchase-order": { group: "Purchases", permission: "PurchaseOrderGenerator" },
  "/inventory/goods-received": { group: "Purchases", permission: "GoodsReceivedNote" },
  // Barcode Stock Audit. The list screen only needs "view"; scan / review / approve /
  // finalize / cancel are gated per-button inside the pages (and again server-side).
  "/inventory/stock-audit": { group: "StockAudit", permission: "StockAuditView" },
  // Stock Manual Consumption. The list only needs "view"; create / edit / cancel / delete are
  // gated per-button inside the pages (and again server-side).
  "/inventory/stock-consumption": { group: "StockConsumption", permission: "StockConsumptionView" },
  "/reports/stock-consumption": { group: "StockConsumption", permission: "StockConsumptionView" },
  // Multi Unit Factor report — anyone who may see the reports hub.
  "/reports/unit-wise-stock": { group: "Reports", permission: "ReportsHub" },
  "/reports": { group: "Reports", permission: "ReportsHub" },
  "/management/setup/feedback": { group: "FeedbackSupport", permission: "FeedbackDashboard" },
  "/management/setup/supporting-applications": {
    group: "SupportingApplications",
    permission: "SupportingApplicationsDownload",
  },
  // Appointments + Commission (opt-in modules). The Setup hub shows these cards by the module switch and
  // the server's Bookings/my-permissions answer (built-in roles carry no permission groups on the web), and
  // /reports does the same for the report card; these entries are the catalog keys behind each path for
  // everything that reads this map. Note: they also join SETUP_HUB_DISTINCT_PERMISSIONS, so the sidebar
  // "Setup" item shows for a custom role holding only these keys (accepted — the hub lists its cards).
  "/appointments": { group: APPOINTMENTS_GROUP, permission: APPOINTMENT_PERMISSIONS.dashboard },
  "/appointments/calendar": { group: APPOINTMENTS_GROUP, permission: APPOINTMENT_PERMISSIONS.view },
  "/appointments/clients": { group: APPOINTMENTS_GROUP, permission: APPOINTMENT_PERMISSIONS.clients },
  "/appointments/staff": { group: APPOINTMENTS_GROUP, permission: APPOINTMENT_PERMISSIONS.staffSchedule },
  "/management/setup/commission": { group: COMMISSION_GROUP, permission: COMMISSION_PERMISSIONS.setup },
  "/reports/commission": { group: COMMISSION_GROUP, permission: COMMISSION_PERMISSIONS.report },
};

const _setupPermDedup = new Map<string, { group: string; permission: string }>();
for (const p of Object.values(SETUP_ROUTE_PERMISSION)) {
  _setupPermDedup.set(`${p.group}\0${p.permission}`, p);
}
_setupPermDedup.set(
  `${SETUP_POS_TERMINAL_PERM.group}\0${SETUP_POS_TERMINAL_PERM.permission}`,
  SETUP_POS_TERMINAL_PERM
);

/** Distinct permissions represented on the setup dashboard (for sidebar “Setup” visibility). */
export const SETUP_HUB_DISTINCT_PERMISSIONS: ReadonlyArray<{ group: string; permission: string }> = [
  ..._setupPermDedup.values(),
];

/**
 * Setup cards gated by their card key instead of their path: the Purchases section points at everyday
 * screens whose paths stay OUT of SETUP_ROUTE_PERMISSION on purpose — a mapped path would start gating
 * the Modern Dashboard's Purchase tile and would make the Setup hub itself appear for purchase-only
 * roles. Same catalog keys as the sidebar's Inventory menu (Sales Return History reuses Sales History
 * the same way Purchase Return History reuses Purchase History).
 */
const SETUP_CARD_KEY_PERMISSION: Record<string, { group: string; permission: string }> = {
  purchase: { group: "Purchases", permission: "PurchaseInventory" },
  "purchase-history": { group: "Purchases", permission: "PurchaseHistory" },
  "purchase-return": { group: "Purchases", permission: "InventoryReturn" },
  "purchase-return-history": { group: "Purchases", permission: "PurchaseHistory" },
  // Stock section: the transfer screen's path stays unmapped for the same reasons.
  "stock-transfer": { group: "Stock", permission: "StockTransfer" },
};

export function resolveSetupCardPermission(card: {
  path: string | null;
  setupCardKey?: string;
}): { group: string; permission: string } | null {
  if (card.setupCardKey === "pos-terminal") return SETUP_POS_TERMINAL_PERM;
  if (card.setupCardKey && SETUP_CARD_KEY_PERMISSION[card.setupCardKey]) return SETUP_CARD_KEY_PERMISSION[card.setupCardKey];
  if (card.path && SETUP_ROUTE_PERMISSION[card.path]) return SETUP_ROUTE_PERMISSION[card.path];
  return null;
}

/**
 * Can the signed-in user open this Setup-hub route? Same rule as the hub's cards (bypass roles,
 * else the mapped catalog permission; unmapped routes are open). For cross-links between modules.
 */
export function canOpenSetupRoute(path: string): boolean {
  const { lookup, bypass } = getNavPermissionContext();
  const perm = SETUP_ROUTE_PERMISSION[path];
  if (!perm || bypass) return true;
  return hasPermission(lookup, perm.group, perm.permission);
}

/** Sidebar / search: combine role checks (caller) with catalog permissions. */
export function canSeeNavMenuItem(
  item: {
    cloudPosPermission?: CloudPosPermSpec;
    requiresSetupHubAccess?: boolean;
  },
  lookup: Map<string, Set<string>>,
  bypass: boolean
): boolean {
  if (item.requiresSetupHubAccess) {
    return canAccessSetupHub(lookup, bypass, SETUP_HUB_DISTINCT_PERMISSIONS);
  }
  return meetsCloudPosPermission(lookup, bypass, item.cloudPosPermission);
}

/** Role names that always get full menu access (company owner / admin). Matches the bypass set. */
const OWNER_BYPASS_ROLE_NAMES = ['developer', 'storeowner', 'companyadmin'];

function rolesIncludeLower(roles: ReadonlyArray<string>, targetLower: string): boolean {
  return roles.some((r) => r.trim().toLowerCase() === targetLower);
}

/** A company owner / admin who must always see every (non-developer) menu item. */
export function isOwnerLikeRole(roles: ReadonlyArray<string>): boolean {
  return OWNER_BYPASS_ROLE_NAMES.some((t) => rolesIncludeLower(roles, t));
}

/**
 * Single source of truth for menu-item visibility, used by the sidebar, global search and the
 * post-login redirect so all three agree. Rules:
 *  - Developer-only items stay restricted to actual Developers (never exposed to company owners).
 *  - Company owners/admins (StoreOwner / CompanyAdmin / Developer) always see every non-developer item.
 *  - Items carrying a catalog permission (cloudPosPermission / requiresSetupHubAccess) are shown when
 *    that permission is granted OR the legacy role-name whitelist matches — so standard roles never
 *    regress and custom company roles gain access purely via the permissions they were granted.
 *  - Items without a catalog permission fall back to the role-name whitelist.
 */
export function passesMenuAccessGate(
  item: {
    roles: string[];
    cloudPosPermission?: CloudPosPermSpec;
    requiresSetupHubAccess?: boolean;
  },
  roles: ReadonlyArray<string>,
  lookup: Map<string, Set<string>>,
  bypass: boolean
): boolean {
  const isDeveloperItem = item.roles.length === 1 && item.roles[0] === 'Developer';
  if (isDeveloperItem) return rolesIncludeLower(roles, 'developer');

  if (isOwnerLikeRole(roles)) return true;

  const roleOk = item.roles.some((role) => roles.includes(role));
  if (item.cloudPosPermission || item.requiresSetupHubAccess) {
    return canSeeNavMenuItem(item, lookup, bypass) || roleOk;
  }
  return roleOk;
}

/**
 * The platform's standard roles (lower case). Any other role is one of the company's own custom
 * roles (their physical names carry a "#c{companyId}" suffix, so they never collide with these).
 */
const STANDARD_ROLE_NAMES = ['admin', 'manager', 'cashier', 'staff', 'storeowner', 'companyadmin', 'developer'];

/** A report route's OWN feature permission from the route map — never the generic Reports hub key. */
function reportRoutePermission(path: string): { group: string; permission: string } | null {
  const perm = SETUP_ROUTE_PERMISSION[path] ?? SETUP_ROUTE_PERMISSION[path.replace(/\/+$/, '')];
  if (!perm || (perm.group === 'Reports' && perm.permission === 'ReportsHub')) return null;
  return perm;
}

/**
 * Can the signed-in user see this report — a card on the Reports hub, an entry of the header's
 * Reports menu? The catalog's Reports group has a single key (ReportsHub), so the per-report rule
 * lives here. A report is visible when:
 *  1. the user is a company owner / admin / developer (isOwnerLikeRole);
 *  2. one of the user's roles is on the report's role list;
 *  3. the report's route has its own feature permission in the route map (anything but ReportsHub,
 *     e.g. Recipe Mapping → InventoryProducts:Production) and it is granted;
 *  4. the user holds NO standard role (only custom company roles), ReportsHub is granted or the
 *     permission checks are bypassed, and the report is in the management tier (its role list has
 *     "Manager") — so custom roles never get the owner / admin-only reports.
 * Menu visibility only: report URLs are not route-guarded (app-wide convention).
 */
export function canSeeReportCard(
  report: { roles: ReadonlyArray<string>; path: string },
  roles: ReadonlyArray<string>,
  lookup: Map<string, Set<string>>,
  bypass: boolean
): boolean {
  const userRoles = roles.map((r) => r.trim().toLowerCase()).filter(Boolean);
  if (isOwnerLikeRole(userRoles)) return true;

  const reportRoles = report.roles.map((r) => r.trim().toLowerCase());
  if (userRoles.some((r) => reportRoles.includes(r))) return true;

  const ownPermission = reportRoutePermission(report.path);
  if (ownPermission && hasPermission(lookup, ownPermission.group, ownPermission.permission)) return true;

  const onlyCustomRoles = userRoles.length > 0 && !userRoles.some((r) => STANDARD_ROLE_NAMES.includes(r));
  return (
    onlyCustomRoles &&
    (bypass || hasPermission(lookup, 'Reports', 'ReportsHub')) &&
    reportRoles.includes('manager')
  );
}

export function getNavPermissionContext(): {
  lookup: Map<string, Set<string>>;
  bypass: boolean;
} {
  const parsed = parsePermissionGroupsFromStorage();
  const bypass = shouldBypassCloudPosPermissionChecks(parsed);
  const lookup =
    parsed && parsed.length > 0 ? buildPermissionLookup(parsed) : new Map<string, Set<string>>();
  return { lookup, bypass };
}
